import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useFplId } from "../context/FplIdContext";
import { fetchFplLive, fetchManagerInfo, fetchFPLData, fetchInjuryNews } from "../api/fplApi";

const REFRESH_MS = 60000;
const fmtRank = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());
const fmtMoney = (v) => (v === null || v === undefined ? "—" : `£${(Number(v) / 10).toFixed(1)}m`);

function useCountUp(target, dur = 700) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const from = prev.current;
    const to = Number(target) || 0;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setVal(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return val;
}

export default function MyTeam() {
  const { fplId, manager: cachedManager, clearFplId } = useFplId();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedGw, setSelectedGw] = useState(null);
  const [latestStartedGw, setLatestStartedGw] = useState(1);
  const [live, setLive] = useState(null);
  const [manager, setManager] = useState(cachedManager);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emptyState, setEmptyState] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [injuryNews, setInjuryNews] = useState([]);

  const selectedEvent = useMemo(() => events.find((e) => Number(e.id) === Number(selectedGw)) || null, [events, selectedGw]);
  const now = Date.now();
  const selectedStarted = selectedEvent?.deadline_time ? new Date(selectedEvent.deadline_time).getTime() <= now : Number(selectedGw) <= Number(latestStartedGw);
  const isLatestGw = Number(selectedGw) === Number(latestStartedGw);

  const livePoints = live?.totals?.total_after_hits ?? live?.entry_history?.points ?? 0;
  const officialGwPoints = live?.entry_history?.points ?? livePoints;
  const overallPoints = live?.entry_history?.total_points ?? manager?.summary_overall_points ?? 0;
  const overallRank = live?.entry_history?.overall_rank ?? manager?.summary_overall_rank ?? null;
  const gwRank = live?.entry_history?.rank ?? null;
  const transfers = live?.entry_history?.event_transfers ?? 0;
  const transferCost = live?.entry_history?.event_transfers_cost ?? live?.transfer_hits ?? 0;
  const pointsOnBench = live?.entry_history?.points_on_bench ?? 0;
  const teamValue = live?.entry_history?.value;
  const bank = live?.entry_history?.bank;
  const chip = live?.active_chip ?? null;
  const averageScore = selectedEvent?.average_entry_score ?? null;
  const highestScore = selectedEvent?.highest_score ?? null;

  const players = useMemo(() => {
    if (!live) return [];
    if (Array.isArray(live.squad) && live.squad.length) return live.squad;
    if (Array.isArray(live?.xi_after_autosubs)) {
      const xi = live.xi_after_autosubs.map((p, i) => ({ ...p, position: p.position ?? i + 1, is_bench: false }));
      const bench = Array.isArray(live?.bench)
        ? live.bench.map((p, i) => ({ ...p, position: p.position ?? 12 + i, is_bench: true }))
        : [];
      return [...xi, ...bench];
    }
    return live?.players ?? live?.picks ?? live?.elements ?? [];
  }, [live]);

  const injuryById = useMemo(() => {
    const map = new Map();
    injuryNews.forEach((p) => {
      const id = Number(p.id ?? p.element ?? p.player_id);
      if (Number.isFinite(id)) map.set(id, p);
    });
    return map;
  }, [injuryNews]);

  const squadAlerts = useMemo(() => players
    .map((p) => ({ player: p, info: injuryById.get(Number(p.id ?? p.element)) }))
    .filter(({ info }) => info && ((info.status && info.status !== "a") || info.news)), [players, injuryById]);

  const captain = players.find((p) => p.is_captain || p.captain);
  const captainName = captain?.name || live?.captain?.name || live?.captainName || "—";
  const animated = useCountUp(livePoints);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const boot = await fetchFPLData();
        const ev = boot?.events || boot?.data?.events || [];
        const started = ev
          .filter((e) => e?.deadline_time && new Date(e.deadline_time).getTime() <= Date.now())
          .sort((a, b) => Number(b.id) - Number(a.id));
        const current = ev.find((e) => e.is_current);
        const latest = current?.id || started[0]?.id || 1;
        if (!alive) return;
        setEvents(ev);
        setLatestStartedGw(latest);
        setSelectedGw(latest);
      } catch {
        if (alive) {
          setLatestStartedGw(1);
          setSelectedGw(1);
        }
      }
    })();
    return () => { alive = false; };
  }, []);


  useEffect(() => {
    let alive = true;
    fetchInjuryNews()
      .then((data) => {
        if (!alive) return;
        setInjuryNews(Array.isArray(data) ? data : data?.data || data?.elements || []);
      })
      .catch(() => { if (alive) setInjuryNews([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (manager || !fplId) return;
    fetchManagerInfo(fplId).then((m) => m && setManager(m)).catch(() => {});
  }, [fplId, manager]);

  const load = useCallback(async (isRefresh = false) => {
    if (!fplId || !selectedGw) return;
    if (selectedEvent?.deadline_time && new Date(selectedEvent.deadline_time).getTime() > Date.now()) {
      setLive(null);
      setEmptyState(`GW${selectedGw} team data will be available after the Gameweek deadline.`);
      setError("");
      setLoading(false);
      return;
    }
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await fetchFplLive(fplId, selectedGw);
      const hasData = Array.isArray(data?.squad) || Array.isArray(data?.xi_after_autosubs) || Array.isArray(data?.players) || Array.isArray(data?.picks);
      if (data?.error || !hasData) {
        setLive(null);
        setEmptyState(`No team data is available for GW${selectedGw}.`);
      } else {
        setLive(data);
        setEmptyState("");
        setLastUpdated(new Date());
      }
      setError("");
    } catch {
      setLive(null);
      setEmptyState(`No team data is available for GW${selectedGw} yet.`);
      if (!isRefresh) setError("");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [fplId, selectedGw, selectedEvent]);

  useEffect(() => {
    if (!selectedGw) return;
    load(false);
    if (!isLatestGw || !selectedStarted) return;
    let timer = setInterval(() => !document.hidden && load(true), REFRESH_MS);
    const onVis = () => !document.hidden && load(true);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [selectedGw, isLatestGw, selectedStarted, load]);

  const switchTeam = () => { clearFplId(); navigate("/onboarding"); };
  const starters = players.filter((p) => Number(p.position || 0) <= 11 && !p.is_bench);
  const benchPlayers = players.filter((p) => Number(p.position || 0) > 11 || p.is_bench).sort((a,b)=>Number(a.position||99)-Number(b.position||99));
  const rows = [1,2,3,4].map((type) => starters.filter((p) => Number(p.element_type) === type));
  const canPrev = Number(selectedGw) > 1;
  const canNext = Number(selectedGw) < 38;

  return (
    <div className="fpl-official-team-page">
      <section className="fpl-myteam-heading">
        <div className="fpl-myteam-nameblock"><h1>{manager?.team_name || "Your team"}</h1><p>{manager?.manager_name}</p></div>
        <div className="fpl-myteam-gw-title">Gameweek {selectedGw || "—"}</div>
        <button className="fpl-switch-team" onClick={switchTeam}>Switch team</button>
      </section>

      <div className="fpl-gw-nav fpl-gw-nav-simple">
        <button disabled={!canPrev} onClick={() => setSelectedGw((g) => Math.max(1, Number(g)-1))}>‹ Previous</button>
        <span>GW{selectedGw || "—"}</span>
        <button disabled={!canNext} onClick={() => setSelectedGw((g) => Math.min(38, Number(g)+1))}>Next ›</button>
      </div>

      {live && <>
        <section className="fpl-myteam-summary">
          <SummaryStat label="GW Points" value={animated} accent />
          <SummaryStat label="Overall Points" value={overallPoints} />
          <SummaryStat label="GW Rank" value={fmtRank(gwRank)} />
          <SummaryStat label="Overall Rank" value={fmtRank(overallRank)} />
          <SummaryStat label="Transfers" value={transfers} sub={transferCost ? `-${transferCost} pts` : "No hit"} />
        </section>
        <section className="fpl-myteam-extra-stats">
          <MiniStat label="Average" value={averageScore ?? "—"} />
          <MiniStat label="Highest" value={highestScore ?? "—"} />
          <MiniStat label="Bench pts" value={pointsOnBench} />
          <MiniStat label="Team value" value={fmtMoney(teamValue)} />
          <MiniStat label="In the bank" value={fmtMoney(bank)} />
          <MiniStat label="Captain" value={captainName} />
        </section>
        {squadAlerts.length > 0 && (
          <section className="fpl-squad-alerts">
            <div className="fpl-squad-alerts-title">
              <strong>{squadAlerts.length === 1 ? "1 player needs attention" : `${squadAlerts.length} players need attention`}</strong>
              <span>Availability from official FPL player news</span>
            </div>
            <div className="fpl-squad-alert-list">
              {squadAlerts.map(({ player, info }) => {
                const chance = info.chance_of_playing_next_round ?? info.chance_of_playing_this_round;
                const status = info.status === "i" ? "Injured" : info.status === "d" ? "Doubtful" : info.status === "s" ? "Suspended" : "Unavailable";
                return (
                  <div className="fpl-squad-alert-item" key={player.id || player.element}>
                    <b>{player.name || player.web_name}</b>
                    <span>{status}{chance != null ? ` · ${chance}% chance to play` : ""}</span>
                    {info.news && <small>{info.news}</small>}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </>}

      {error && <div className="fpl-myteam-warning">{error}</div>}

      {emptyState && !loading ? (
        <section className="fpl-myteam-gw-empty">
          <div className="fpl-empty-gw-number">GW{selectedGw}</div>
          <h2>{Number(selectedGw) > Number(latestStartedGw) ? "Gameweek not open yet" : "Gameweek data unavailable"}</h2>
          <p>{emptyState}</p>
          {Number(selectedGw) > Number(latestStartedGw) && <span>Your GW1 history will stay here. Use Previous to return to completed/active Gameweeks.</span>}
        </section>
      ) : (
        <section className="fpl-official-pitch-card">
          <div className="fpl-pitch-toolbar">
            <div><b>{isLatestGw ? "Live team" : `GW${selectedGw} team`}</b><span>{isLatestGw ? "Updates during matches" : "Gameweek history"}</span></div>
            {isLatestGw && <div className="fpl-live-status"><i className={refreshing ? "updating" : ""} />{refreshing ? "Updating" : "Live"}</div>}
          </div>

          {loading ? <div className="fpl-pitch-loading">Loading your GW{selectedGw} squad…</div> : (
            <div className="fpl-official-pitch">
              <div className="fpl-pitch-brand"><span>FPL NEPAL</span><span>GW{selectedGw}</span></div>
              <div className="fpl-pitch-markings" aria-hidden="true"><i className="box top"/><i className="circle"/><i className="half"/><i className="box bottom"/></div>
              <div className="fpl-lineup">
                {rows.map((row, idx) => row.length > 0 && (
                  <div className={`fpl-lineup-row pos-${idx+1}`} key={idx}>
                    {row.map((p) => <OfficialPlayer key={p.id || p.element} p={p} chip={chip} injury={injuryById.get(Number(p.id ?? p.element))} />)}
                  </div>
                ))}
              </div>
              <div className="fpl-bench-zone">
                <div className="fpl-bench-label">Substitutes</div>
                <div className="fpl-bench-four">{benchPlayers.map((p, i) => <OfficialPlayer key={p.id || p.element} p={p} bench order={i+1} chip={chip} injury={injuryById.get(Number(p.id ?? p.element))} />)}</div>
              </div>
            </div>
          )}
        </section>
      )}

      {live && <div className="fpl-myteam-footnote">
        {lastUpdated && isLatestGw ? `Updated ${lastUpdated.toLocaleTimeString()} · ` : ""}{isLatestGw ? "Live bonus is provisional. Autosubs can change until fixtures are finalized." : `Showing saved official FPL data for GW${selectedGw}.`}{chip ? ` · ${String(chip).toUpperCase()}` : ""}
      </div>}
    </div>
  );
}

function SummaryStat({ label, value, accent=false, sub }) {
  return <div className={accent ? "is-accent" : ""}><strong>{value}</strong><span>{label}</span>{sub && <small>{sub}</small>}</div>;
}
function MiniStat({ label, value }) { return <div><span>{label}</span><strong>{value}</strong></div>; }
const shirtUrl = (teamCode) => teamCode ? `https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${teamCode}-220.webp` : "";
function OfficialPlayer({ p, bench=false, order, chip, injury }) {
  const name = p.name || p.web_name || "Player";
  const pts = Number(p.points ?? p.total_points ?? p.gwPoints ?? 0);
  const played = Number(p.minutes || 0) > 0 || pts !== 0;
  const isCap = Boolean(p.is_captain || p.captain);
  const isVice = Boolean(p.is_vice || p.is_vice_captain);
  const mult = isCap ? (chip === "3xc" ? 3 : 2) : 1;
  const shownPts = pts * mult;
  const flagged = injury && ((injury.status && injury.status !== "a") || injury.news);
  const chance = injury?.chance_of_playing_next_round ?? injury?.chance_of_playing_this_round;
  return (
    <div className={`fpl-official-player ${bench ? "is-bench" : ""}`}>
      {bench && <span className="fpl-bench-order">{order}</span>}
      <div className="fpl-player-shirt-wrap"><img src={shirtUrl(p.team_code)} alt="" />{isCap && <b className="fpl-armband">C</b>}{!isCap && isVice && <b className="fpl-armband vice">V</b>}{flagged && <b className="fpl-player-flag" title={injury.news || "Player availability warning"}>!</b>}</div>
      <div className="fpl-player-name" title={name}>{name}</div>
      <div className={`fpl-player-live-score ${played ? "has-played" : ""}`}>{played ? `${shownPts} pts` : "Yet to play"}</div>{flagged && <div className="fpl-player-availability">{chance != null ? `${chance}%` : "Flagged"}</div>}
    </div>
  );
}
